import { FlexRow, HeaderText } from '@make-software/cspr-design';
import {
  NavLink,
  Outlet,
  useLocation,
  generatePath,
  useParams,
  useOutletContext
} from 'react-router-dom';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { App, getAppById } from 'app/console-api';
import {
  APPS_DEALS_PATH,
  APPS_OFFERS_PATH,
  APPS_PATH,
  APPS_STATISTICS_PATH,
  APPS_TASKS_PATH,
  APPS_VIEW_PATH
} from 'app/route/paths';
import { LayoutContainer } from 'app/components/common/common-styles';
import { StyledTableHeader } from 'app/components/common/layout/styled-elements';

const Wrap = styled.header(({ theme }) => ({
  background: theme.styleguideColors.backgroundSecondary
}));

const TabsBar = styled.nav(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 24,
  borderBottom: `1px solid ${theme.styleguideColors.borderPrimary}`,
  minHeight: 56
}));

const NavTabs = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 16,
  color: theme.styleguideColors.fillPrimaryBlue
}));

const NavSpan = styled.span(({ theme }) => ({
  color: theme.styleguideColors.fillPrimaryBlue
}));

const TabLink = styled(NavLink)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 56,
  padding: '0 2px',
  textDecoration: 'none',
  fontWeight: 400,
  fontSize: 16,
  color: theme.styleguideColors.contentSecondary,
  borderBottom: '2px solid transparent',
  transition: 'color .15s ease, border-color .15s ease, opacity .15s ease',

  ':hover': {
    color: theme.styleguideColors.contentPrimary,
    borderBottomColor: theme.styleguideColors.borderSecondary
  },

  // Active route (React Router adds aria-current="page")
  '&[aria-current="page"]': {
    color: theme.styleguideColors.fillPrimaryBlue,
    borderBottomColor: theme.styleguideColors.fillPrimaryBlue,
    fontWeight: 500
  },

  // Keyboard focus
  ':focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.styleguideColors.fillSecondaryBlueHover}`,
    borderRadius: 4
  },

  // Disabled appearance (optional)
  "&[aria-disabled='true']": {
    color: theme.styleguideColors.contentQuaternary,
    pointerEvents: 'none',
    opacity: 0.6
  }
}));

const DynamicNavTab = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 16,
  color: theme.styleguideColors.fillPrimaryBlue,
  cursor: 'pointer'
}));

const LastCrumb = styled.span(({ theme }) => ({
  color: theme.styleguideColors.contentSecondary
}));

export type Crumb = { label: React.ReactNode; to?: string };

type NavState = { app?: App };
type TabsCtx = {
  app: App | null;
  setApp: (a: App) => void;
  appID: string;
  setBreadcrumbs: (extras: Crumb[]) => void;
};
export const useTabsCtx = () => useOutletContext<TabsCtx>();

export function TabsPageLayout() {
  const { appID = '' } = useParams();
  const location = useLocation();

  // your existing app state wiring (sidebar state + fallback fetch)...
  const [app, setApp] = React.useState<App | null>(null);
  const passedApp = (location.state as NavState | undefined)?.app ?? null;
  useEffect(() => {
    if (passedApp && passedApp.id === appID) setApp(passedApp);
  }, [appID]);

  const breadcrumbsAppearance =
    location.pathname.includes('create') ||
    location.pathname.includes('edit') ||
    location.pathname.includes('view') ||
    location.pathname.includes('rewards') ||
    location.pathname.includes('categories');

  useEffect(() => {
    if (app) return;
    getAppById(appID).then((fetched) => {
      setApp(fetched);
    });
  }, [app, appID]);

  const currentTab = React.useMemo(() => {
    const p = location.pathname;
    if (p.includes('/tasks'))
      return { label: 'My tasks', to: generatePath(APPS_TASKS_PATH, { appID }) };
    if (p.includes('/offers'))
      return { label: 'Traffic offers', to: generatePath(APPS_OFFERS_PATH, { appID }) };
    if (p.includes('/deals'))
      return { label: 'Deals', to: generatePath(APPS_DEALS_PATH, { appID }) };
    if (p.includes('/settings'))
      return { label: 'Settings', to: generatePath(APPS_VIEW_PATH, { appID }) };
    if (p.includes('/statistics'))
      return { label: 'Statistics', to: generatePath(APPS_STATISTICS_PATH, { appID }) };
    return { label: 'Preview', to: generatePath(APPS_PATH, { appID }) };
  }, [location.pathname, appID]);

  // ⭐️ Store extras per tab key
  const [extrasByTab, setExtrasByTab] = React.useState<Record<string, Crumb[]>>({});

  // Children call this; it writes only to the current tab's bucket
  const setBreadcrumbs = React.useCallback(
    (arr: Crumb[]) => {
      setExtrasByTab((prev) => ({ ...prev, [currentTab.label]: arr }));
    },
    [currentTab.label]
  );

  // Read extras for current tab; no reset effect required
  const extras = extrasByTab[currentTab.label] ?? [];

  const base: Crumb[] = [
    { label: app?.name ?? 'App', to: generatePath('/apps/:appID', { appID }) },
    currentTab
  ];

  const crumbs = [...base, ...extras];

  return (
    <LayoutContainer justify={'start'} align={'start'} gap={32}>
      {breadcrumbsAppearance ? (
        <FlexRow gap={8} wrap={'wrap'}>
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return c.to && !isLast ? (
              <React.Fragment key={i}>
                <DynamicNavTab to={c.to}>{c.label}</DynamicNavTab>
                <NavSpan>›</NavSpan>
              </React.Fragment>
            ) : (
              <LastCrumb key={i} aria-current={isLast ? 'page' : undefined}>
                {c.label}
              </LastCrumb>
            );
          })}
        </FlexRow>
      ) : (
        <header>
          <StyledTableHeader justify={'space-between'}>
            <HeaderText size={1} scale={'xs'}>
              {app?.name ?? 'Loading…'}
            </HeaderText>
          </StyledTableHeader>
          <Wrap>
            <TabsBar aria-label="Tabs">
              <TabLink to="." end>
                Preview
              </TabLink>
              <TabLink to={generatePath(APPS_TASKS_PATH, { appID })}>My tasks</TabLink>
              <TabLink to={generatePath(APPS_OFFERS_PATH, { appID })}>Traffic offers</TabLink>
              <TabLink to={generatePath(APPS_DEALS_PATH, { appID })}>Deals</TabLink>
              <TabLink to={generatePath(APPS_STATISTICS_PATH, { appID })}>Statistics</TabLink>
              <TabLink to={generatePath(APPS_VIEW_PATH, { appID })}>Settings</TabLink>
            </TabsBar>
          </Wrap>
        </header>
      )}

      {/* Pages receive setters via Outlet context */}
      <Outlet context={{ app, setApp, appID, setBreadcrumbs } satisfies TabsCtx} />
    </LayoutContainer>
  );
}
